import React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import noop from 'lodash/noop';
import { ULABEL_CONFIG } from '../../../../common/constant/constant';
import { useAPIProviderGetSignedUrl } from '../../../../common/hooks/use-api-provider';
import MediaErrorComponent from '../../../../common/media-error';
import type { GetULabelConfigFunction } from '../../../../common/providers/feature-flag-provider';
import { multiVideoListAtom, selectedVideoAtom } from '../../../labelling-v2/atoms/multi-video-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { THUMBNAIL_VIDEO_ID } from '../../constants';
import { useMediaState } from '../../providers/hooks/use-media-state';
import type { MediaItemT } from '../../types';
import { Loader } from '../video/components/loader';
import { Video } from '../video/components/video';
import { MEDIA_MESSAGES } from '../video/constants/error-messages';
import { useMediaLoadingErrorEvents } from '../video/hooks/use-video-loading-error-events';
type PropsT = {
  getULabelConfig: GetULabelConfigFunction | null;
  hoveredFrame: number;
};
export function VideoThumbnailContainer({
  getULabelConfig,
  hoveredFrame
}: PropsT) {
  const {
    isRefreshingSource,
    isAnyLoading,
    mediaLoadingError,
    handleLoading,
    handleError
  } = useMediaLoadingErrorEvents();
  const getSignedUrl = useAPIProviderGetSignedUrl();
  const {
    getFrameNumber,
    getFrameTime,
    frameRate
  } = useMediaState();
  const {
    imageSettingsAtom
  } = useStageAtoms();
  const signedUrlTTLValue = getULabelConfig?.(ULABEL_CONFIG.SIGNED_URL_TTL);
  const srcExpirationDuration = typeof signedUrlTTLValue === 'number' ? signedUrlTTLValue : null;
  const setMultiVideoList = useSetAtom(multiVideoListAtom);
  const selectedVideo = useAtomValue(selectedVideoAtom);
  const mediaUrl = selectedVideo?.url;
  const {
    brightness,
    contrast
  } = useAtomValue(imageSettingsAtom);
  if (!mediaUrl) {
    return null;
  }
  const staticProps = {
    disablePointerEvents: true,
    mediaFrameRate: frameRate,
    videoType: 'video/mp4',
    controls: false,
    preload: ('auto' as const),
    playbackRate: 1,
    volume: 1,
    isPlaying: false,
    fitToScreen: noop,
    onFrameLoadStatus: noop,
    onLoadedMetadata: noop,
    onDimensionChange: noop,
    onEndToLastFrame: noop,
    onLoad: noop,
    fetchFileSizeAndFormat: undefined
  };
  const handleSrcChange = (src: string, mediaItem: MediaItemT) => {
    setMultiVideoList(prev => {
      return prev.map(item => {
        if (item.referenceUri === mediaItem.referenceUri) {
          return {
            ...item,
            url: src
          };
        }
        return item;
      });
    });
  };
  const showLoading = isAnyLoading;
  const showError = !showLoading && !!mediaLoadingError;
  return <>
      <Video {...staticProps} id={THUMBNAIL_VIDEO_ID} src={mediaUrl} brightness={brightness} contrast={contrast} currentFrame={hoveredFrame} getTimeFromFrameNumber={getFrameTime || null} getFrameNumberFromTime={getFrameNumber || null} onSrcChange={src => {
      handleSrcChange(src, (selectedVideo as MediaItemT));
    }} onLoading={handleLoading} onError={handleError} getSignedUrl={getSignedUrl || null} srcExpirationDuration={srcExpirationDuration} />

      {showError && <MediaErrorComponent errorMessage={mediaLoadingError?.message} />}

      {showLoading && <Loader message={isRefreshingSource ? MEDIA_MESSAGES.REFRESHING_URL_MESSAGE : MEDIA_MESSAGES.DEFAULT_LOADING_MESSAGE} />}
    </>;
}